import { Color } from "@material-ui/core";
import { alpha, createTheme } from "@material-ui/core/styles";

import { componentOverrides } from "./componentOverrides";
import { cssCustomProperties, fontFamilies, layoutTokens, legacyColors, semanticColors } from "./designTokens";

export const theme = createTheme({
  spacing: 8,
  palette: {
    semantic: semanticColors,
    common: { black: semanticColors["text/primary"], white: semanticColors["text/inverse"] },
    divider: semanticColors["border/default"],
    primary: {
      light: semanticColors["surface/base"],
      main: semanticColors["action/primary"],
      dark: semanticColors["action/primary-pressed"],
      contrastText: semanticColors["text/inverse"],
    },
    secondary: {
      light: semanticColors["surface/base"],
      main: semanticColors["action/secondary"],
      dark: semanticColors["action/secondary-hover"],
      contrastText: semanticColors["text/inverse"],
    },
    tertiary: {
      light: semanticColors["surface/base"],
      main: semanticColors["action/secondary"],
      dark: semanticColors["text/primary"],
      contrastText: semanticColors["text/inverse"],
    },
    error: { main: semanticColors["status/error"], light: semanticColors["status/error-bg"] },
    // warning はNEWバッジやオレンジCTAにも使われるため、用途の移行まで旧色を保持。
    warning: legacyColors.warning,
    background: { default: semanticColors["surface/base"], paper: semanticColors["surface/card"] },
    success: { main: semanticColors["status/success"], light: semanticColors["status/success-bg"] },
    info: { main: semanticColors["status/info"], light: semanticColors["status/info-bg"] },
    text: {
      primary: semanticColors["text/primary"],
      secondary: semanticColors["text/secondary"],
      disabled: semanticColors["text/disabled"],
    },
    action: {
      active: semanticColors["icon/strong"],
      hover: alpha(semanticColors["text/primary"], 0.04),
      selected: semanticColors["state/selected-bg"],
      disabled: semanticColors["text/disabled"],
      disabledBackground: semanticColors["surface/base"],
      focus: alpha(semanticColors["action/primary"], 0.12),
    },
    // 旧ランプのキーは互換用。v2の画面は palette.semantic の用途名を参照する。
    grey: {
      50: semanticColors["surface/subtle"],
      100: semanticColors["surface/base"],
      200: semanticColors["border/default"],
      300: semanticColors["border/default"],
      400: semanticColors["border/strong"],
      500: semanticColors["icon/default"],
      600: semanticColors["icon/strong"],
      700: semanticColors["text/secondary"],
      800: semanticColors["action/secondary"],
      900: semanticColors["text/primary"],
      A100: semanticColors["surface/base"],
      A200: semanticColors["border/default"],
      A400: semanticColors["border/strong"],
      A700: semanticColors["text/secondary"],
    },
    transparent: semanticColors["surface/overlay"],
    chart: legacyColors.chart,
    black: {
      50: semanticColors["surface/subtle"],
      100: semanticColors["surface/base"],
      200: semanticColors["border/default"],
      300: semanticColors["border/strong"],
      400: semanticColors["text/disabled"],
      500: semanticColors["icon/default"],
      600: semanticColors["icon/strong"],
      700: semanticColors["text/secondary"],
      800: semanticColors["action/secondary"],
      900: semanticColors["text/primary"],
    },
    blue: {
      50: semanticColors["surface/subtle"],
      100: semanticColors["state/selected-bg"],
      200: semanticColors["state/selected-bg"],
      300: semanticColors["chart/income-2"],
      400: semanticColors["chart/income-2"],
      500: semanticColors["action/primary"],
      600: semanticColors["action/primary"],
      700: semanticColors["action/primary"],
      800: semanticColors["action/primary-hover"],
      900: semanticColors["action/primary-pressed"],
    },
    // v1維持のstatusランプと、後続PRで用途別に移すオレンジ。
    red: legacyColors.red,
    orange: legacyColors.orange,
    green: legacyColors.green,
    yellow: legacyColors.yellow,
  },
  typography: {
    fontFamily: fontFamilies.body,
    h1: {
      fontSize: "32px",
      fontWeight: 700,
      lineHeight: "130%",
      "@media (max-width:600px)": {
        fontSize: "24px", // SP用のスタイル(デフォルトのbreakpoint sm=600px)
      },
    },
    h2: {
      fontSize: "24px",
      fontWeight: 700,
      lineHeight: "130%",
      "@media (max-width:600px)": {
        fontSize: "20px", // SP用のスタイル(デフォルトのbreakpoint sm=600px)
      },
    },
    h3: {
      fontSize: "20px",
      fontWeight: 700,
      lineHeight: "130%",
      "@media (max-width:600px)": {
        fontSize: "18px", // SP用のスタイル(デフォルトのbreakpoint sm=600px)
      },
    },
    h4: {
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: "130%",
      "@media (max-width:600px)": {
        fontSize: "14px", // SP用のスタイル(デフォルトのbreakpoint sm=600px)
      },
    },
    h5: {
      fontSize: "14px",
      fontWeight: 700,
      lineHeight: "130%",
      "@media (max-width:600px)": {
        fontSize: "12px", // SP用のスタイル(デフォルトのbreakpoint sm=600px)
      },
    },
    h6: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "130%",
      "@media (max-width:600px)": {
        fontSize: "10px", // SP用のスタイル(デフォルトのbreakpoint sm=600px)
      },
    },
    body1: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "150%",
      "@media (max-width:600px)": {
        fontSize: "14px", // SP用のスタイル(デフォルトのbreakpoint sm=600px)
      },
    },
    body2: {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "150%",
      "@media (max-width:600px)": {
        fontSize: "12px", // SP用のスタイル(デフォルトのbreakpoint sm=600px)
      },
    },
    button: {
      fontSize: "16px",
      fontWeight: 500,
      lineHeight: "130%",
      "@media (max-width:600px)": {
        fontSize: "14px", // SP用のスタイル(デフォルトのbreakpoint sm=600px)
      },
    },
    caption: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "150%",
      "@media (max-width:600px)": {
        fontSize: "10px", // SP用のスタイル(デフォルトのbreakpoint sm=600px)
      },
    },
  },
  breakpoints: {
    values: layoutTokens.breakpoints,
  },
  // MUI components override styles
  overrides: {
    ...componentOverrides,
    MuiCssBaseline: { "@global": { ":root": cssCustomProperties } },
  },
});

export default theme;

// Create a theme instance.

// Material-UIのパレットに新しい色を追加するために、型を拡張します
declare module "@material-ui/core/styles/createPalette" {
  interface Palette {
    semantic: typeof semanticColors;
    black: Color;
    red: Color;
    orange: Color;
    green: Color;
    blue: Color;
    yellow: Color;
    transparent: string;
    tertiary: Palette["primary"];
    chart: {
      income: {
        primary: string;
        darker: string;
        dark: string;
      };
      expense: {
        primary: string;
        darker: string;
        dark: string;
        light: string;
        lighter: string;
        lightest: string;
      };
    };
  }
  interface PaletteOptions {
    semantic?: typeof semanticColors;
    black?: ColorPartial;
    red?: ColorPartial;
    orange?: ColorPartial;
    green?: ColorPartial;
    blue?: ColorPartial;
    yellow?: ColorPartial;
    transparent?: string;
    tertiary?: Palette["primary"];
    chart?: {
      income?: {
        primary?: string;
        darker?: string;
        dark?: string;
      };
      expense?: {
        primary?: string;
        darker?: string;
        dark?: string;
        light?: string;
        lighter?: string;
        lightest?: string;
      };
    };
  }
}
